import {
  MONTH_PERIOD,
  SIX_MONTH_PERIOD,
  YEAR_PERIOD,
} from 'modules/upsell/lib/selfUpsell/plans/utils'
import {
  ChargeType,
  Currency,
  PeriodUnit,
  PricingModel,
  Tier,
} from 'modules/upsell/lib/selfUpsell/types'

export default class ChargebeeAddon {
  private _price: number

  constructor(
    public _id: string,
    private _currency: Currency,
    private _period: number,
    private _periodUnit: PeriodUnit,
    private _pricingModel: PricingModel,
    private _chargeType: ChargeType,
    private _selectedCountries: string[] | undefined,
    price: number | null,
    private _tiers: Tier[] = []
  ) {
    this._price = price || 0
  }

  get currency() {
    return this._currency
  }

  get periodNumber() {
    return this._period
  }

  get periodUnit() {
    return this._periodUnit
  }

  get tiers(): Tier[] {
    return this._tiers
  }

  private _handlePriceResult(price: number) {
    const priceResult = price / this._period

    if (this._periodUnit === YEAR_PERIOD) {
      return Math.round((priceResult / 12) * 100) / 100
    }
    if (this._periodUnit === MONTH_PERIOD) {
      return priceResult
    }
    if (this._periodUnit === SIX_MONTH_PERIOD) {
      return Math.round((priceResult / 6) * 100) / 100
    }
  }

  isCountryPlan(isoCountryCode: string) {
    return this._selectedCountries?.includes(isoCountryCode)
  }

  isRegionPlan(region: string) {
    return this._id.includes(`-${region}-`)
  }

  isF25Compatible(isoCountryCode: string) {
    return this._id.includes(`-${isoCountryCode}-`)
  }

  isCompatible(isoCountryCode: string, region: string) {
    return this.isCountryPlan(isoCountryCode) || this.isRegionPlan(region)
  }

  isPeriodPlan(isAnnually: boolean) {
    return isAnnually ? this._periodUnit === YEAR_PERIOD : this._periodUnit === MONTH_PERIOD
  }

  getPrice(seats = 1) {
    if (!this.isRecurring()) return 0

    if (this.isPerSeat()) {
      return (seats * this._price) / 100
    }

    if (this.isFee()) {
      return this._price / 100
    }

    if (this.isTiered()) {
      let price = 0
      let currentTier: Tier | undefined

      for (let seat = 1; seat <= seats; seat++) {
        if (!currentTier || seat > (currentTier?.endingUnit || Infinity)) {
          currentTier = this._tiers.find(
            (tier) => seat >= (tier.startingUnit || 0) && seat <= (tier.endingUnit || Infinity)
          )
        }

        if (currentTier) {
          price += currentTier.price
        }
      }

      return price / 100
    }

    if (this.isStairstep()) {
      const price = this._tiers[0]?.price || 0
      return price / 100
    }
  }

  getMonthlyPrice(seats = 1) {
    if (this._chargeType !== 'recurring') return 0
    const price = this.getPrice(seats)
    if (!price) return
    return this._handlePriceResult(price)
  }

  isFee(): boolean {
    return this._pricingModel === 'flat_fee'
  }

  isPerSeat(): boolean {
    return this._pricingModel === 'per_unit'
  }

  isTiered(): boolean {
    return this._pricingModel === 'tiered'
  }

  isStairstep(): boolean {
    return this._pricingModel === 'stairstep'
  }

  isRecurring(): boolean {
    return this._chargeType === 'recurring'
  }

  getFlatFeeMonthlyPrice() {
    if (!this.isRecurring()) return 0
    if (!this.isPerSeat()) return 0
    return this._handlePriceResult(this._price / 100)
  }

  getPerSeatMonthlyPrice(seats = 1) {
    if (!this.isRecurring()) return 0
    if (!this.isFee()) return 0
    return this._handlePriceResult((seats * this._price) / 100)
  }
}
